<template>
    <div class="password-reset-container">
        <div class="logo-section">
            <img 
                src="@/assets/action-logo.svg" 
                alt="Action Network Logo" 
                class="main-logo"
            >
        </div>
        
        <h1 class="reset-title">Reset Your Password</h1>
        
        <!-- Loading state -->
        <div v-if="loading" class="loading-container">
            <p>Verifying reset token...</p>
        </div>
        
        <!-- Invalid token state -->
        <div v-else-if="!tokenValid" class="error-container">
            <h3>Invalid Reset Link</h3>
            <p>This password reset link is invalid or has expired.</p>
            <p>Please request a new password reset from the login page.</p>
            <button @click="goToLogin" class="btn btn-primary">Go to Login</button>
        </div>
        
        <!-- Valid token - password reset form -->
        <div v-else class="reset-form-container">
            <div class="user-info" v-if="userInfo">
                <p>Resetting password for:</p>
                <p class="user-name"><strong>{{ userInfo.first_name }} {{ userInfo.last_name }}</strong></p>
                <p class="user-email">{{ userInfo.email }}</p>
            </div>
            
            <form @submit.prevent="resetPassword" class="reset-form">
                <div class="form-group">
                    <label for="newPassword">New Password</label>
                    <input
                        id="newPassword"
                        type="password"
                        v-model="newPassword"
                        required
                        placeholder="Enter your new password"
                        class="form-input"
                        :class="{ 'error': passwordError }"
                    >
                    
                    <!-- Password strength indicator -->
                    <div v-if="newPassword" class="password-strength">
                        <div class="strength-bar">
                            <div 
                                class="strength-fill" 
                                :class="passwordStrength.class"
                                :style="{ width: (passwordStrength.score / 5) * 100 + '%' }"
                            ></div>
                        </div>
                        <span class="strength-text" :class="passwordStrength.class">
                            {{ passwordStrength.text }}
                        </span>
                    </div>
                </div>
                
                <div class="form-group">
                    <label for="confirmPassword">Confirm New Password</label>
                    <input
                        id="confirmPassword"
                        type="password"
                        v-model="confirmPassword"
                        required
                        placeholder="Confirm your new password"
                        class="form-input"
                        :class="{ 'error': passwordError }"
                    >
                </div>
                
                <button type="submit" class="btn btn-primary" :disabled="submitting">
                    {{ submitting ? 'Resetting Password...' : 'Reset Password' }}
                </button>
            </form>
        </div>
        
        <!-- Success state -->
        <div v-if="resetSuccess" class="success-container">
            <h3>Password Reset Successful!</h3>
            <p>Your password has been successfully reset.</p>
            <p>You can now log in with your new password.</p>
            <button @click="goToLogin" class="btn btn-primary">Go to Login</button>
        </div>
        
        <!-- Error messages -->
        <div v-if="error" class="error-container">
            <p class="error-message">{{ error }}</p>
            <ul v-if="errorDetails && errorDetails.length" class="error-details">
                <li v-for="detail in errorDetails" :key="detail">{{ detail }}</li>
            </ul>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { passwordResetService } from '@/services/passwordResetService';

export default {
    name: 'PasswordReset',
    setup() {
        const router = useRouter();
        
        // Reactive data
        const loading = ref(true);
        const tokenValid = ref(false);
        const userInfo = ref(null);
        const newPassword = ref('');
        const confirmPassword = ref('');
        const submitting = ref(false);
        const resetSuccess = ref(false);
        const error = ref('');
        const errorDetails = ref([]);
        const resetToken = ref('');
        
        // Computed properties
        const passwordError = computed(() => {
            return newPassword.value && confirmPassword.value && 
                   newPassword.value !== confirmPassword.value;
        });
        
        const passwordStrength = computed(() => {
            return passwordResetService.getPasswordStrength(newPassword.value);
        });
        
        // Methods
        const verifyToken = async () => {
            try {
                loading.value = true;
                resetToken.value = passwordResetService.getTokenFromURL();
                
                if (!resetToken.value) {
                    tokenValid.value = false;
                    return;
                }
                
                const result = await passwordResetService.verifyToken(resetToken.value);
                
                if (result.success) {
                    tokenValid.value = true;
                    userInfo.value = result.user;
                } else {
                    tokenValid.value = false;
                    error.value = result.error;
                }
            } catch (err) {
                console.error('Token verification error:', err);
                tokenValid.value = false;
                error.value = 'Failed to verify reset token';
            } finally {
                loading.value = false;
            }
        };
        
        const resetPassword = async () => {
            try {
                error.value = '';
                errorDetails.value = [];
                submitting.value = true;
                
                const result = await passwordResetService.resetPassword(
                    resetToken.value,
                    newPassword.value,
                    confirmPassword.value
                );
                
                if (result.success) {
                    resetSuccess.value = true;
                    passwordResetService.clearURLParameters();
                } else {
                    error.value = result.error;
                    errorDetails.value = result.details || [];
                }
            } catch (err) {
                console.error('Password reset error:', err);
                error.value = 'An unexpected error occurred';
            } finally {
                submitting.value = false;
            }
        };
        
        const goToLogin = () => {
            router.push('/login');
        };
        
        // Lifecycle
        onMounted(() => {
            verifyToken();
        });
        
        return {
            loading,
            tokenValid,
            userInfo,
            newPassword,
            confirmPassword,
            submitting,
            resetSuccess,
            error,
            errorDetails,
            passwordError,
            passwordStrength,
            resetPassword,
            goToLogin
        };
    }
};
</script>

<style scoped>
.password-reset-container {
    max-width: 500px;
    margin: 40px auto;
    padding: 40px 20px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 80vh;
    justify-content: center;
}

.logo-section {
    margin-bottom: 30px;
    width: 100%;
    height: auto;
    display: flex;
    justify-content: center;
    align-items: center;
}

.main-logo {
    width: 200px;
    height: auto;
    margin-bottom: 20px;
}

.reset-title {
    font-size: 2.2rem;
    color: #2c3e50;
    margin-bottom: 30px;
    font-weight: 600;
}

.loading-container {
    padding: 40px;
    color: #666;
    font-size: 1.1rem;
}

.reset-form-container {
    width: 100%;
    max-width: 400px;
}

.user-info {
    background-color: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 30px;
    border-left: 4px solid #4285f4;
}

.user-info p {
    margin: 5px 0;
}

.user-name {
    font-size: 1.1rem;
}

.user-email {
    color: #666;
    font-size: 0.9rem;
}

.reset-form {
    text-align: left;
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 5px;
    color: #2c3e50;
    font-weight: 500;
}

.form-input {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 16px;
    transition: border-color 0.2s;
    box-sizing: border-box;
}

.form-input:focus {
    outline: none;
    border-color: #4285f4;
}

.form-input.error {
    border-color: #dc3545;
}

.password-strength {
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.strength-bar {
    flex: 1;
    height: 4px;
    background-color: #e9ecef;
    border-radius: 2px;
    overflow: hidden;
}

.strength-fill {
    height: 100%;
    transition: all 0.3s ease;
}

.strength-fill.weak {
    background-color: #dc3545;
}

.strength-fill.fair {
    background-color: #fd7e14;
}

.strength-fill.good {
    background-color: #ffc107;
}

.strength-fill.strong {
    background-color: #28a745;
}

.strength-text {
    font-size: 0.85rem;
    font-weight: 500;
    min-width: 60px;
}

.strength-text.weak {
    color: #dc3545;
}

.strength-text.fair {
    color: #fd7e14;
}

.strength-text.good {
    color: #ffc107;
}

.strength-text.strong {
    color: #28a745;
}

.btn {
    padding: 12px 24px;
    border: none;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    text-decoration: none;
    display: inline-block;
    text-align: center;
}

.btn-primary {
    background-color: #4285f4;
    color: white;
    width: 100%;
}

.btn-primary:hover:not(:disabled) {
    background-color: #357abd;
}

.btn-primary:disabled {
    background-color: #ccc;
    cursor: not-allowed;
}

.success-container {
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
    border-radius: 8px;
    padding: 30px;
    color: #155724;
}

.success-container h3 {
    margin: 0 0 15px 0;
    color: #155724;
}

.success-container p {
    margin: 10px 0;
}

.success-container .btn {
    margin-top: 20px;
    width: auto;
    padding: 10px 30px;
}

.error-container {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    border-radius: 8px;
    padding: 20px;
    margin-top: 20px;
    color: #721c24;
}

.error-container h3 {
    margin: 0 0 15px 0;
}

.error-message {
    margin: 0 0 10px 0;
    font-weight: 500;
}

.error-details {
    text-align: left;
    padding-left: 20px;
    margin: 10px 0 0 0;
}

.error-details li {
    margin: 5px 0;
}

@media (max-width: 768px) {
    .password-reset-container {
        padding: 20px;
    }
    
    .reset-title {
        font-size: 1.8rem;
    }
    
    .main-logo {
        width: 150px;
    }
}
</style>